import { useCallback, useEffect, useState } from 'react'
import classNames from 'classnames'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/Container'
import Image from '@/components/base/Image'
import ImageParallax from '@/components/base/ImageParallax'
import SVGIcon from '@/components/base/SVGIcon'

import { Section, WPImage } from '@/types'

type GalleryProgressProps = {
  emblaApi?: EmblaCarouselType
}

const GalleryProgress = ({ emblaApi }: GalleryProgressProps) => {
  const [scrollProgress, setScrollProgress] = useState(0)

  const onScroll = useCallback((emblaApi: EmblaCarouselType) => {
    const progress = Math.max(0, Math.min(1, emblaApi.scrollProgress()))
    setScrollProgress(progress * 1)
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onScroll(emblaApi)
    emblaApi
      .on('reInit', onScroll)
      .on('scroll', onScroll)
      .on('slideFocus', onScroll)
  }, [emblaApi, onScroll])

  return (
    <div className="relative h-[.1rem] w-[28rem] overflow-hidden bg-neutral-90 md:w-[33rem] lg:w-[26.5rem]">
      <div
        className="absolute left-0 top-0 h-full w-full origin-left bg-pink"
        style={{ transform: `scale(${scrollProgress})` }}
      />
    </div>
  )
}

type GalleryButtonsProps = {
  emblaApi?: EmblaCarouselType
}

const GalleryButtons = ({ emblaApi }: GalleryButtonsProps) => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true)
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true)

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollPrev()
  }, [emblaApi])

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return
    emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev())
    setNextBtnDisabled(!emblaApi.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  return (
    <div className="hidden space-x-xs md:flex">
      <div>
        <button
          type="button"
          disabled={prevBtnDisabled}
          onClick={onPrevButtonClick}
          className={classNames(
            'relative h-[4.4rem] w-[4.4rem] rounded-full bg-pink disabled:opacity-30',
            'ease-in-out transition-opacity duration-300'
          )}
          aria-label="Prev"
        >
          <SVGIcon
            symbol="chevron-left"
            className="absolute left-1/2 top-1/2 h-[1.7rem] w-[1.7rem] -translate-x-1/2 -translate-y-1/2 fill-none stroke-white"
          />
        </button>
      </div>
      <div>
        <button
          type="button"
          disabled={nextBtnDisabled}
          onClick={onNextButtonClick}
          className={classNames(
            'relative h-[4.4rem] w-[4.4rem] rounded-full bg-pink disabled:opacity-30',
            'ease-in-out transition-opacity duration-300'
          )}
          aria-label="Next"
        >
          <SVGIcon
            symbol="chevron-right"
            className="absolute left-1/2 top-1/2 h-[1.7rem] w-[1.7rem] -translate-x-1/2 -translate-y-1/2 fill-none stroke-white"
          />
        </button>
      </div>
    </div>
  )
}

type GalleryProps = Section & {
  collection?: {
    image: WPImage
  }[]
}

const Gallery = ({ collection, className, ...props }: GalleryProps) => {
  const [ref, emblaApi] = useEmblaCarousel({ align: 'start' })

  return (
    <section className={classNames('overflow-hidden', className)} {...props}>
      <Container className="flex flex-col space-y-xs">
        <div ref={ref}>
          <div className="-ml-gutter flex">
            {collection?.map(({ image }, index) => (
              <div
                key={`slide-${index}`}
                className="min-w-0 flex-shrink-0 flex-grow-0 basis-[80%] pl-gutter"
              >
                <ImageParallax className="rounded-xs md:rounded-sm lg:rounded-md">
                  {(ref) => (
                    <div className="aspect-h-[169] aspect-w-[280] lg:aspect-h-[660] lg:aspect-w-[995]">
                      <Image
                        fill
                        ref={ref}
                        size="large"
                        loading="lazy"
                        source={image}
                        alt={image?.alt}
                        className="rounded-xs md:rounded-sm lg:rounded-md"
                      />
                    </div>
                  )}
                </ImageParallax>
              </div>
            ))}
          </div>
        </div>
        <div className="flex items-center md:space-x-xs">
          <GalleryButtons emblaApi={emblaApi} />
          <GalleryProgress emblaApi={emblaApi} />
        </div>
      </Container>
    </section>
  )
}

export default Gallery
